import { useEffect, useState } from 'react'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

export default function Animation33({ animation, badge, all_animations }: Props) {
  const [smokeClearing, setSmokeClearing] = useState(false)
  const [showBadge, setShowBadge] = useState(false)

  useEffect(() => {
    setTimeout(() => setSmokeClearing(true), 300)
    setTimeout(() => setShowBadge(true), 1500)
  }, [])

  const smokeParticles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    delay: i * 0.1,
    x: Math.cos((i * 30 * Math.PI) / 180) * 100,
    y: Math.sin((i * 30 * Math.PI) / 180) * 100
  }))

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes smokeDisperse {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 0.8;
          }
          100% {
            transform: translate(var(--tx), var(--ty)) scale(2);
            opacity: 0;
          }
        }
        @keyframes badgeReveal {
          0% {
            transform: scale(0.5);
            opacity: 0;
            filter: blur(10px);
          }
          100% {
            transform: scale(1);
            opacity: 1;
            filter: blur(0);
          }
        }
        .smoke-particle {
          animation: smokeDisperse 1.5s ease-out forwards;
        }
        .badge-reveal {
          animation: badgeReveal 0.8s ease-out forwards;
        }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh]">
        <div className="relative">
          {/* Particules de fumée */}
          {smokeClearing && smokeParticles.map(particle => (
            <div
              key={particle.id}
              className="absolute w-20 h-20 rounded-full bg-tessan-cream/40 smoke-particle blur-xl"
              style={{
                '--tx': `${particle.x}px`,
                '--ty': `${particle.y}px`,
                animationDelay: `${particle.delay}s`,
                left: '50%',
                top: '50%',
                marginLeft: '-40px',
                marginTop: '-40px'
              } as React.CSSProperties}
            />
          ))}

          {/* Badge qui apparaît */}
          <div className={`relative z-10 ${showBadge ? 'badge-reveal' : 'opacity-0'}`}>
            <div className="w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream rounded-2xl flex items-center justify-center shadow-2xl shadow-tessan-yellow/50">
              <span className="text-6xl sm:text-8xl">{badge.emoji}</span>
            </div>
          </div>

          {/* Fumée initiale */}
          {!smokeClearing && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 rounded-full bg-tessan-cream/60 blur-2xl animate-pulse" />
            </div>
          )}
        </div>

        <div className={`mt-8 text-center transition-all duration-700 ${showBadge ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="text-2xl sm:text-4xl font-black text-tessan-yellow">{badge.name}</div>
          <div className="text-lg sm:text-xl text-tessan-cream mt-2">+{badge.points_awarded} points</div>
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs">{badge.description}</div>
        </div>
      </div>
    </AnimationLayout>
  )
}
